import React from 'react'
import Helmet from 'react-helmet'
import {Loadable} from 'common/Loadable'
import styles from './RouteShowPage.scss'
import {base} from 'lib/styleguide/base.scss'
import {connect} from 'react-redux'
import {loadRoute, updateRoute, addPhoto, photoRemoved, pinRoute, unpinRoute} from 'lib/api/routes'
import {setPreference} from 'lib/api/currentUser'
import cx from 'classnames'
import _ from 'lodash'
import {PrintLoggedOutDialog, PRINT_LOGGED_OUT_DIALOG} from './dialogs/PrintLoggedOutDialog'
import {PrintUpsellDialog, PRINT_UPSELL_DIALOG} from './dialogs/PrintUpsellDialog'
import {NotDoneYetDialog, NOT_DONE_YET_DIALOG} from 'common/NotDoneYetDialog'
import {ShareDialog, SHARE_DIALOG} from 'common/ShareDialog'
import {EditRouteDialog, EDIT_ROUTE_DIALOG} from './dialogs/EditRouteDialog'
import {ExportDialog, EXPORT_DIALOG} from './dialogs/ExportDialog'
import {CopyRouteDialog, COPY_ROUTE_DIALOG} from './dialogs/CopyRouteDialog'
import {DeleteDialogContainer, DELETE_DIALOG} from './dialogs/DeleteDialog'
import {SaveAsSegmentDialog, SAVE_AS_SEGMENT_DIALOG} from './dialogs/SaveAsSegmentDialog'
import {AddAnnotationDialog, NEW_ANNOTATION_DIALOG} from './dialogs/AddAnnotationDialog'
import {NewRouteDialog, NEW_ROUTE_DIALOG} from './dialogs/NewRouteDialog'
import {PrintOfficialPDFDialog, PRINT_OFFICIAL_PDF_DIALOG} from './dialogs/PrintOfficialPDFDialog'
import {SidePanel} from './SidePanel'
import {MapPanelContainer} from './MapPanel'
import {showGallery} from 'features/gallery/showGallery'
import {GlobalNav} from 'common/GlobalNav'
import {DroppablePage} from 'common/DroppablePage'
import {UpgradeModal} from 'features/recumbent/UpgradeModal'
import {getPhotoUploadMaxConcurrency, checkWebGLSupport, queryStringSet, currentURLWithQueryParams} from 'lib/utils'
import {urls} from 'lib/urls'
import PromisePool from 'es6-promise-pool'
import * as routeUtils from 'lib/routeUtils'
import {MetricUnitsContext} from 'common/units/MetricUnitsContext'
import {apiv3} from 'lib/apiv3'
import {FullscreenButton, FullscreenContext} from 'common/FullscreenButton'
import {trackEvent} from 'lib/trackEvent'
import {CONTENT_COLLAPSE_WIDTH} from 'lib/layoutConstants'
import {AdminMenuItem} from 'common/GlobalNav/AdminMenu'
import {showModal} from 'lib/modalUtils'
import {TROUTE_INFO_MODAL} from 'common/modals/TRouteInfoModal'

const PIN_UPSELL_DIALOG = 'PIN_UPSELL_DIALOG'

const parseQuerySegment = (location) => {
  if (!location || !location.query || !location.query.highlight) {
    return null
  }
  const arr = location.query.highlight.split('-').map(n => parseInt(n, 10))
  if (arr.length === 2 && arr.every(n => isFinite(n))) {
    return arr
  }
  return null
}

export class RouteShowPage extends React.PureComponent {
  constructor (props) {
    super(props)
    // TODO - refactor this.state to a more flexible
    // shape for persisted/hover map interactions
    // hoverItems: [{type, id}]
    // persistedItems: [{type, id}]

    const highlightSlice = parseQuerySegment(props.location)
    const suggestedBounds = highlightSlice
      ? routeUtils.getSliceBounds(this.props.route, highlightSlice)
      : null

    this.state = {
      collapse: window.innerWidth < CONTENT_COLLAPSE_WIDTH,
      isFullscreen: false,
      sgHeight: this.getSgHeight(),
      dialog: null,
      highlightSlice,
      suggestedBounds,
      zoomOnHighlightSliceChange: null,
      hoverSlice: null,
      highlightPoint: null,
      hoverCue: null,
      dirtyRoute: null
    }
    this._throttledResize = _.throttle(this.onResize, 300, {trailing: true})
  }

  componentDidMount () {
    const {route, params, loadRoute} = this.props
    const privacyCode = _.get(this.props, 'location.query.privacy_code')
    const routeId = parseInt(params.routeId, 10)
    if (!route) {
      loadRoute(routeId, privacyCode)
    }

    if (window.matchMedia) {
      // fancy callback way of tracking collapse state that doesn't need debounce
      this._mediaquery = window.matchMedia(`screen and (max-width: ${CONTENT_COLLAPSE_WIDTH}px)`)
      console.log('matchMedia', `screen and (max-width: ${CONTENT_COLLAPSE_WIDTH}px)`)
      this._mediaquery.addListener(this.onResize)
    } else {
      // dumb fallback
      window.addEventListener('resize', this._throttledResize)
    }

    this.showGalleryIfPhotoInURL()
    this.showShareDialogIfShareInURL()
    if (window.ga) {
      window.ga('set', 'page', '/virtual/rspv2' + window.location.pathname + window.location.search)
      window.ga('send', 'pageview')
    }
    try {
      const supportsWebGL = checkWebGLSupport()
      trackEvent({
        category: 'WebGL',
        action: supportsWebGL ? 'SupportDetected' : 'SupportNotDetected',
        label: 'WebGL probe on RSP'
      })
    } catch (e) {
      console.warn('encountered an error attempting to probe WebGL support')
      console.warn(e)
    }
  }

  componentDidUpdate () {
    this.showGalleryIfPhotoInURL()
  }

  componentWillUnmount () {
    // cleanup resize event listeners
    window.removeEventListener('resize', this._throttledResize)
    if (this._mediaquery) {
      this._mediaquery.removeListener(this.onResize)
      delete this._mediaquery
    }
  }

  // This isn't exactly elegant, but it's very functional and allows us to
  // easily support the old fragment-based url scheme for photos without
  // upgrading react-router
  showGalleryIfPhotoInURL () {
    const galleryIsOpen = document.getElementById('#react-gallery') // again, I never said it was elegant
    if (this._didShowGallery || galleryIsOpen) { return } // use a flag to prevent weird state clobbering

    const {route, currentUser} = this.props
    if (route && window.location.hash.match(/#photos\/\d+$/)) {
      this._didShowGallery = true
      const userIsOwner = route.user.id === currentUser.id
      const activePhotoId = parseInt(window.location.hash.match(/#photos\/(\d+)$/)[1])
      const orderedPhotos = _.orderBy(route.photos, 'id', 'desc')
      showGallery({
        photos: orderedPhotos,
        activePhotoId,
        readonly: !userIsOwner,
        parentType: 'route',
        parentId: route.id,
        onPhotoDeleted: this.onPhotoDeleted
      })
    }
  }

  showShareDialogIfShareInURL = () => {
    const {route} = this.props
    if (route && window.location.search.match(/\?openShare=true/)) {
      this.showDialog(SHARE_DIALOG)
    }
  }

  getSgHeight = () => {
    // these constants need to match the values in layout_variables.scss
    const MAX_HEIGHT = 200
    const MIN_HEIGHT = 75
    const proportional = window.innerHeight * 0.2
    return _.clamp(proportional, MIN_HEIGHT, MAX_HEIGHT)
  }

  showDialog = (dialog) => {
    this.setState({dialog})
  }

  hideDialog = () => {
    this.setState({dialog: null})
  }

  // we need to fake a resize event here so that MapPanel and SampleGraph
  // will expand to fill the space the SidePanel is leaving.
  // this IE-inclusive event trigger solution taken from https://stackoverflow.com/a/31899998
  toggleFullscreen = () => {
    var resizeEvent = window.document.createEvent('UIEvents')
    resizeEvent.initUIEvent('resize', true, false, window, 0)
    this.setState({isFullscreen: !this.state.isFullscreen}, () => window.dispatchEvent(resizeEvent))
  }

  fullscreenState = () => {
    const {isFullscreen} = this.state
    return {
      isFullscreen: isFullscreen,
      toggleFullscreen: this.toggleFullscreen
    }
  }

  onResize = () => {
    const {collapse} = this.state
    const iw = window.innerWidth
    const shouldCollapse = iw < CONTENT_COLLAPSE_WIDTH
    const sgHeight = this.getSgHeight()

    if (collapse !== shouldCollapse || this.state.sgHeight !== sgHeight) {
      this.setState({collapse: shouldCollapse, sgHeight})
    }
  }

  onCoursePointHovered = (index) => {
    this.setState({hoverCue: index})
  }

  onCoursePointClicked = (index) => {
    const {selectedCue} = this.state
    if (selectedCue === index) {
      // small hack: force a remount of the selected cue in order to trigger
      // auto-pan behavior
      this.setState({selectedCue: null}, () => {
        this.setState({selectedCue: index})
      })
    } else {
      this.setState({selectedCue: index})
    }
  }

  onSegmentSelected = (segment, zoomOnHighlightSliceChange) => {
    const newState = {highlightSlice: segment, zoomOnHighlightSliceChange}
    segment && queryStringSet(this.props.location, 'highlight', segment.join('-'))
    if (zoomOnHighlightSliceChange) {
      newState.suggestedBounds = routeUtils.getSliceBounds(this.props.route, segment)
    }
    this.setState(newState)
  }

  onSegmentHovered = (segment) => {
    this.setState({hoverSlice: segment})
  }

  onSegmentUnselected = () => {
    queryStringSet(this.props.location, 'highlight', undefined)
    this.setState({highlightSlice: null, suggestedBounds: null})
  }

  onPointHighlighted = (highlightPoint) => {
    this.setState({highlightPoint: highlightPoint})
  }

  printRoute = () => {
    const {route} = this.props

    if (route.get('mapPdf')) {
      this.showDialog(PRINT_OFFICIAL_PDF_DIALOG)
    } else {
      this.doPrint()
    }
  }

  doPrint = () => {
    const {currentUser, params} = this.props
    const {routeId} = params
    const {navigate} = this.props.route._permissions
    if (!currentUser.id) {
      this.showDialog(PRINT_LOGGED_OUT_DIALOG)
    } else if (currentUser.accountLevel > 0 || navigate) {
      window.open(`/routes/${routeId}/print`)
    } else {
      this.showDialog(PRINT_UPSELL_DIALOG)
    }
  }

  flattenElevation = () => {
    const {route} = this.props
    const {highlightSlice, dirtyRoute} = this.state
    const r = dirtyRoute || route

    const withFlattened = routeUtils.flattenElevation(r, highlightSlice[0], highlightSlice[1])
    this.setState({
      dirtyRoute: withFlattened,
      highlightSlice: null
    })
  }

  excludeFromMetrics = () => {
    const {route} = this.props
    const {highlightSlice, dirtyRoute} = this.state
    const r = dirtyRoute || route

    const withExcluded = routeUtils.excludeFromMetrics(r, highlightSlice[0], highlightSlice[1])
    this.setState({
      dirtyRoute: withExcluded,
      highlightSlice: null
    })
  }

  includeInMetrics = () => {
    const {route} = this.props
    const {highlightSlice, dirtyRoute} = this.state
    const r = dirtyRoute || route

    const withIncluded = routeUtils.includeInMetrics(r, highlightSlice[0], highlightSlice[1])
    this.setState({
      dirtyRoute: withIncluded,
      highlightSlice: null
    })
  }

  onSave = () => {
    const {updateRoute} = this.props
    const {dirtyRoute} = this.state

    dirtyRoute && updateRoute(dirtyRoute.get('id'), {
      trackPoints: dirtyRoute.trackPoints,
      rawPoints: dirtyRoute.rawPoints
    }).then(() => this.setState({dirtyRoute: null}))
  }

  onRevert = () => {
    this.setState({dirtyRoute: null})
  }

  onPhotoDeleted = ({photoId}) => {
    const {photoRemoved, route} = this.props
    photoRemoved(route.id, photoId)
  }

  render () {
    const {
      route,
      currentUser,
      pinRoute,
      unpinRoute,
      updateRoute,
      onTriggerFileBrowser,
      pendingPhotoUploads,
      userMetricUnits
    } = this.props

    const {
      collapse,
      isFullscreen,
      sgHeight,
      dialog,
      suggestedBounds,
      highlightSlice,
      zoomOnHighlightSliceChange,
      hoverSlice,
      hoverCue,
      highlightPoint,
      dirtyRoute,
      selectedCue
    } = this.state

    const r = dirtyRoute || route
    const editable = r && r._permissions.can_be_modified && !r.locked
    const privacyCode = r.privacyCode || null
    const metricUnits = currentUser.id
      ? userMetricUnits
      : r.metricUnits

    return (
      <MetricUnitsContext.Provider value={metricUnits}>
        <Helmet title={`${route.name} · Ride with GPS`} />
        <AdminMenuItem
          label='View in TRSP alpha'
          href={currentURLWithQueryParams({USE_TROUTE_SHOW_AS_RSP: true})} />
        <AdminMenuItem
          label='Show route info'
          onClick={() => showModal(TROUTE_INFO_MODAL, {troute: route, objectType: 'route', objectId: route.id})} />
        <div className={cx(base, styles.layout, collapse && styles.collapse)}>
          <GlobalNav />
          <FullscreenContext.Provider value={this.fullscreenState()}>
            <FullscreenButton />
          </FullscreenContext.Provider>
          <div className={cx(styles.RouteShowPage, collapse && styles.collapse)}>
            <SidePanel
              mobile={collapse}
              fullscreen={isFullscreen}
              r={r}
              editable={editable}
              onPhotoDeleted={this.onPhotoDeleted}
              segmentMatches={r.segments}
              pinRoute={pinRoute}
              unpinRoute={unpinRoute}
              printRoute={this.printRoute}
              currentUser={currentUser}
              onCoursePointHovered={this.onCoursePointHovered}
              onCoursePointClicked={this.onCoursePointClicked}
              onSegmentSelected={this.onSegmentSelected}
              onSegmentHovered={this.onSegmentHovered}
              onPointHighlighted={this.onPointHighlighted}
              pendingPhotoUploads={pendingPhotoUploads}
              onTriggerFileBrowser={onTriggerFileBrowser}
              showDialog={this.showDialog} />
            {!collapse && <MapPanelContainer
              route={dirtyRoute || route}
              editable={editable}
              dirtyRoute={dirtyRoute}
              sgHeight={sgHeight}
              isFullscreen={isFullscreen}
              hoverCue={hoverCue}
              selectedCue={selectedCue}
              onCoursePointClicked={this.onCoursePointClicked}
              suggestedBounds={suggestedBounds}
              highlightSlice={highlightSlice}
              zoomOnHighlightSliceChange={zoomOnHighlightSliceChange}
              hoverSlice={hoverSlice}
              highlightPoint={highlightPoint}
              onPointHighlighted={this.onPointHighlighted}
              onSegmentSelected={this.onSegmentSelected}
              onSegmentUnselected={this.onSegmentUnselected}
              flattenElevation={this.flattenElevation}
              excludeFromMetrics={this.excludeFromMetrics}
              includeInMetrics={this.includeInMetrics}
              onSave={this.onSave}
              onRevert={this.onRevert}
              showDialog={this.showDialog} />}
            {dialog === PRINT_LOGGED_OUT_DIALOG &&
              <PrintLoggedOutDialog onRequestClose={this.hideDialog} />}
            {dialog === PRINT_UPSELL_DIALOG &&
              <PrintUpsellDialog onRequestClose={this.hideDialog} routeId={r.id} />}
            {dialog === PIN_UPSELL_DIALOG &&
              <UpgradeModal onRequestClose={this.hideDialog} feature='pinning' />}
            {dialog === EDIT_ROUTE_DIALOG &&
              <EditRouteDialog updateRoute={updateRoute} onRequestClose={this.hideDialog} route={dirtyRoute || route} />}
            {dialog === NOT_DONE_YET_DIALOG &&
              <NotDoneYetDialog onRequestClose={this.hideDialog} />}
            {dialog === EXPORT_DIALOG &&
              <ExportDialog onRequestClose={this.hideDialog} troute={r} objectType='route' />}
            {dialog === COPY_ROUTE_DIALOG &&
              <CopyRouteDialog
                route={r}
                defaultVisibility={currentUser.preferences.get('default_privacy_route', 1)}
                onRequestClose={this.hideDialog} />}
            {dialog === SHARE_DIALOG &&
              <ShareDialog onRequestClose={this.hideDialog}
                objectType='route'
                objectId={r.id}
                objectTitle={r.name}
                privacyCode={privacyCode}
                objectUrl={urls.routes.show(r.id, privacyCode)} />}
            {dialog === DELETE_DIALOG && <DeleteDialogContainer onRequestClose={this.hideDialog}
              route={r} />}
            {dialog === SAVE_AS_SEGMENT_DIALOG && <SaveAsSegmentDialog
              highlightSlice={highlightSlice}
              route={r}
              onRequestClose={this.hideDialog} />}
            {dialog === NEW_ANNOTATION_DIALOG && <AddAnnotationDialog
              highlightSlice={highlightSlice}
              route={r}
              onRequestClose={this.hideDialog} />}
            {dialog === NEW_ROUTE_DIALOG && <NewRouteDialog
              highlightSlice={highlightSlice}
              route={r}
              onRequestClose={this.hideDialog} />}
            {dialog === PRINT_OFFICIAL_PDF_DIALOG && <PrintOfficialPDFDialog
              route={r}
              doPrint={this.doPrint}
              onRequestClose={this.hideDialog} />}
          </div>
        </div>
      </MetricUnitsContext.Provider>
    )
  }
}

class DroppableRouteShowPage extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      pendingUploads: 0
    }
  }

  componentDidMount () {
    // recording the recentActivity here because the RouteShowPage component
    // gets double mounted based on userIsOwner below
    const {routeId} = this.props.params
    routeId && this.props.currentUser.id && apiv3.users.recordActivity('viewed', 'Route', routeId)
  }

  onDrop = (files) => {
    const {route, addPhoto} = this.props
    this.setState((prevState) => ({pendingUploads: prevState.pendingUploads + files.length}))
    let index = 0
    const nextUpload = () => {
      if (!files[index]) { return null }
      const nextUpload = files[index]
      index++
      return addPhoto(route.id, nextUpload)
        .then(() => this.setState((prevState) => ({pendingUploads: prevState.pendingUploads - 1})))
    }
    const pool = new PromisePool(nextUpload, getPhotoUploadMaxConcurrency())

    pool.start()
  }

  onTriggerFileBrowser = () => {
    this._dropzone && this._dropzone.open()
  }

  render () {
    const {currentUser, route} = this.props
    const {pendingUploads} = this.state
    const userIsOwner = route && (currentUser.id === route.user.id)
    return userIsOwner
      ? <DroppablePage
        ref={r => { this._dropzone = r }}
        multiple
        onDrop={this.onDrop}>
        <RouteShowPage {...this.props} onTriggerFileBrowser={this.onTriggerFileBrowser} pendingPhotoUploads={pendingUploads} />
      </DroppablePage>
      : <RouteShowPage {...this.props} />
  }
}

class LoadableRouteShowPage extends React.PureComponent {
  render () {
    const props = this.props
    const ensureRouteLoaded = () => props.loadRoute(props.params.routeId, props.privacyCode)
    return <Loadable
      {...props}
      resourceLoaded={!!props.route || ensureRouteLoaded}
      component={DroppableRouteShowPage}
    />
  }
}

// TODO: right now, we use a hack to pull server-populated segment matches from
// the sparseroute. We need to grab these from an endpoint instead.
const mapStateToProps = (state, ownProps) => {
  const routeId = parseInt(ownProps.params.routeId, 10)
  const privacyCode = _.get(ownProps, 'location.query.privacy_code')
  return {
    route: state.routes.get(routeId),
    privacyCode,
    currentUser: state.currentUser,
    userMetricUnits: state.currentUser.preferences.get('metric_units')
  }
}

const mapDispatchToProps = {
  loadRoute,
  updateRoute,
  addPhoto,
  photoRemoved,
  pinRoute,
  unpinRoute,
  setPreference
}

export const RouteShowPageContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableRouteShowPage)
